import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertDialog, Button, Flag, Heading, Notification } from '@lumx/react';
import { ProgressionBar } from './shared/ProgressionBar';
import { mdiFileImport } from '@mdi/js';
import { buildTree, collectAllNodeIds } from '../utils/treeUtils';
import { useDragDrop } from '../hooks/useDragDrop';
import { useWizard } from '../context/WizardContext';
import { useOrgData } from '../context/OrgDataContext';
import OrgStructurePane from './OrgStructurePane';
import SitesPane from './SitesPane';
import GroupsPage from './GroupsPage';
import MobileConfigConflictDialog from './dialogs/MobileConfigConflictDialog';

const OrgAssignmentPage: React.FC = () => {
  const { reportSiteProgress, setNavGuard, pendingNavTarget, proceedNav, cancelNav } = useWizard();
  const {
    nodes, setMobileConfig,
    sites, assignSite, unassignSite, bulkAssignSites,
    mobileConfigs, globalMobileConfigId, setGlobalMobileConfigId,
    isDirty, saveDraft,
  } = useOrgData();

  // Register nav guard while on this page — block navigation when dirty
  useEffect(() => {
    setNavGuard(() => !isDirty);
    return () => setNavGuard(null);
  }, [isDirty, setNavGuard]);

  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [sitesExpandedIds, setSitesExpandedIds] = useState<Set<string>>(new Set());

  const toggleSites = useCallback((id: string) =>
    setSitesExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    }), []);
  const [isGlobalExpanded, setIsGlobalExpanded] = useState(true);
  const [orgSearch, setOrgSearch] = useState('');
  const [showSaveToast, setShowSaveToast] = useState(false);
  const [viewingGroups, setViewingGroups] = useState<{ kind: 'node' | 'site'; id: string; name: string } | null>(null);

  const [mobileConflict, setMobileConflict] = useState<{
    nodeId: string; configId: string; fromNodeName: string;
  } | null>(null);

  // ── Derived ────────────────────────────────────────────────
  const nodesWithGlobal = useMemo(() => [
    { id: '__global__', name: 'Global / Platform', type: 'OrganizationalUnit' as const, parentId: null, mobileConfigId: globalMobileConfigId, createdAt: '', updatedAt: '' },
    ...nodes,
  ], [nodes, globalMobileConfigId]);

  const assignedCount = useMemo(() => sites.filter(s => s.assignedNodeId !== null).length, [sites]);
  const totalCount = sites.length;

  useEffect(() => {
    reportSiteProgress(assignedCount, totalCount);
  }, [assignedCount, totalCount, reportSiteProgress]);

  const unassignedMobileConfigs = useMemo(() => {
    const assignedIds = new Set<string | null>([
      globalMobileConfigId,
      ...nodes.map(n => n.mobileConfigId),
    ].filter(Boolean));
    return mobileConfigs.filter(c => !assignedIds.has(c.id));
  }, [mobileConfigs, nodes, globalMobileConfigId]);

  // ── DnD (site-to-OU only — OU reparenting disabled on this page) ──
  const dnd = useDragDrop({
    onSiteDropOnOU: (siteId, nodeId) => {
      assignSite(siteId, nodeId);
      setExpandedIds(prev => new Set([...prev, nodeId]));
    },
    onOUReparent: () => {},
    onOUReorder: () => {},
    getNodes: () => nodes,
  });

  const handleDropOnOU = useCallback((e: React.DragEvent, nodeId: string) => {
    e.preventDefault();
    const payload = dnd.getDragPayload();
    if (payload?.kind === 'site') dnd.handleSiteDropOnOU(e, nodeId);
  }, [dnd]);

  const handleDragOverOU = useCallback((e: React.DragEvent, nodeId: string) => {
    const payload = dnd.getDragPayload();
    if (payload?.kind === 'site') {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      dnd.highlightDropTarget(nodeId);
    }
  }, [dnd]);

  const handleGlobalDragOver = useCallback((e: React.DragEvent) => {
    const payload = dnd.getDragPayload();
    if (payload?.kind === 'site') {
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
      dnd.highlightDropTarget('__global__');
    }
  }, [dnd]);

  const handleGlobalDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const payload = dnd.getDragPayload();
    dnd.handleDragEnd();
    if (payload?.kind === 'site') assignSite(payload.id, '__global__');
  }, [dnd, assignSite]);

  // ── Tree expand/collapse ───────────────────────────────────
  const toggleExpand = useCallback((id: string) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const tree = buildTree(nodes, []);
    setExpandedIds(new Set(collectAllNodeIds(tree)));
    setIsGlobalExpanded(true);
    const nodeIdsWithSites = new Set(sites.filter(s => s.assignedNodeId).map(s => s.assignedNodeId as string));
    setSitesExpandedIds(nodeIdsWithSites);
  }, [nodes, sites]);

  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
    setSitesExpandedIds(new Set());
  }, []);

  // ── Mobile config ─────────────────────────────────────────
  const handleMobileConfigChange = useCallback((nodeId: string, configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => {
    if (conflict) {
      setMobileConflict({ nodeId, configId: conflict.configId, fromNodeName: conflict.fromNodeName });
      return;
    }
    setMobileConfig(nodeId, configId);
    if (configId) setGlobalMobileConfigId(prev => prev === configId ? null : prev);
  }, [setMobileConfig]);

  const handleGlobalMobileConfigChange = useCallback((configId: string | null, conflict: { configId: string; fromNodeName: string } | null) => {
    if (conflict) {
      setMobileConflict({ nodeId: '__global__', configId: conflict.configId, fromNodeName: conflict.fromNodeName });
      return;
    }
    setGlobalMobileConfigId(configId);
    if (configId) setMobileConfig('__clear_config__' as any, configId);
  }, [setMobileConfig]);

  const confirmMobileConflict = useCallback(() => {
    if (!mobileConflict) return;
    const { nodeId, configId } = mobileConflict;
    if (nodeId === '__global__') {
      setGlobalMobileConfigId(configId);
      setMobileConfig('__remove_conflict__' as any, configId);
    } else {
      setGlobalMobileConfigId(prev => prev === configId ? null : prev);
      setMobileConfig(nodeId, configId);
    }
    setMobileConflict(null);
  }, [mobileConflict, setMobileConfig]);

  // ── Bulk assign ───────────────────────────────────────────
  const handleBulkAssign = useCallback((siteIds: string[], nodeId: string) => {
    bulkAssignSites(siteIds, nodeId);
  }, [bulkAssignSites]);

  // ── Render ────────────────────────────────────────────────
  const conflictNode = mobileConflict ? nodesWithGlobal.find(n => n.id === mobileConflict.nodeId) : null;
  const conflictConfig = mobileConflict ? mobileConfigs.find(c => c.id === mobileConflict.configId) : null;

  if (viewingGroups) {
    if (viewingGroups.kind === 'site') {
      return (
        <GroupsPage
          nodeId={viewingGroups.id}
          nodeName={viewingGroups.name}
          hasSitesMapped
          kind="site"
          onBack={() => setViewingGroups(null)}
        />
      );
    }
    const hasSitesMapped = viewingGroups.id === '__global__'
      ? true
      : sites.some(s => s.assignedNodeId === viewingGroups.id);
    return (
      <GroupsPage
        nodeId={viewingGroups.id}
        nodeName={viewingGroups.name}
        hasSitesMapped={hasSitesMapped}
        onBack={() => setViewingGroups(null)}
      />
    );
  }

  return (
    <div style={{ display: 'contents' }} onDragEnd={dnd.handleDragEnd}>

      <main className="main-content" style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {/* Page header */}
        <div className="page-header">
          <div className="page-header-left">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Heading as="h1" typography="headline">Org mapping</Heading>
              <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.60)' }}>Map sites and mobile configurations to Org units &amp; Locations.</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              {/* Sites progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <ProgressionBar
                  percents={{ progress: assignedCount, total: totalCount }}
                  maxWidth="120px"
                />
                <Flag
                  color={assignedCount === totalCount ? 'green' : 'yellow'}
                  label={`${assignedCount} / ${totalCount}`}
                />
                <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.60)', whiteSpace: 'nowrap' }}>Sites mapped</span>
              </div>
              <div style={{ width: 1, height: 20, background: 'rgba(0,0,0,0.12)' }} />
              {/* Mobile configs progress */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {(() => {
                  const assigned = mobileConfigs.length - unassignedMobileConfigs.length;
                  const total = mobileConfigs.length;
                  return (
                    <Flag
                      color={assigned === total ? 'green' : 'yellow'}
                      label={`${assigned} / ${total}`}
                    />
                  );
                })()}
                <span style={{ fontSize: 13, color: 'rgba(0,0,0,0.60)', whiteSpace: 'nowrap' }}>Mobile configs mapped</span>
              </div>
            </div>
          </div>
          <div className="page-header-right">
            <Button emphasis="low" size="m" leftIcon={mdiFileImport}>Import JSON</Button>
            <Button emphasis="high" onClick={() => { saveDraft(); setShowSaveToast(true); setTimeout(() => setShowSaveToast(false), 3000); }}>Save</Button>
          </div>
        </div>

        {/* Two-pane layout */}
        <div className="mapping-layout">
          <OrgStructurePane
            nodes={nodes}
            sites={sites}
            mobileConfigs={mobileConfigs}
            nodesWithGlobal={nodesWithGlobal}
            globalMobileConfigId={globalMobileConfigId}
            expandedIds={expandedIds}
            searchQuery={orgSearch}
            addingChildOf="none"
            dragOverId={dnd.dragOverId}
            draggingId={dnd.draggingId}
            dragInsert={dnd.dragInsert}
            isGlobalExpanded={isGlobalExpanded}
            onSearchChange={setOrgSearch}
            onToggleExpand={toggleExpand}
            onExpandAll={expandAll}
            onCollapseAll={collapseAll}
            onSetGlobalExpanded={setIsGlobalExpanded}
            onAddChild={() => {}}
            onAddChildSave={() => {}}
            onAddChildCancel={() => {}}
            onRename={() => {}}
            onConvertType={() => {}}
            onDeleteRequest={() => {}}
            onMobileConfigChange={handleMobileConfigChange}
            onGlobalMobileConfigChange={handleGlobalMobileConfigChange}
            sitesExpandedIds={sitesExpandedIds}
            onSitesToggle={toggleSites}
            onViewUsers={id => {
              const node = nodesWithGlobal.find(n => n.id === id);
              if (node) setViewingGroups({ kind: 'node', id, name: node.name });
            }}
            onViewSiteGroups={(siteId, siteName) => setViewingGroups({ kind: 'site', id: siteId, name: siteName })}
            onDragStart={(e, kind, id) => dnd.handleDragStart(e, kind, id)}
            onOUDragOver={handleDragOverOU}
            onOUDrop={handleDropOnOU}
            onDragLeave={dnd.handleDragLeave}
            onGlobalDragOver={handleGlobalDragOver}
            onGlobalDrop={handleGlobalDrop}
            onSiteDropOnOU={handleDropOnOU}
            onUnassign={unassignSite}
            readOnlyStructure
          />

          <SitesPane
            sites={sites}
            nodes={nodesWithGlobal}
            draggingId={dnd.draggingId}
            onDragStart={(e, kind, id) => dnd.handleDragStart(e, kind, id)}
            onAssign={assignSite}
            onUnassign={unassignSite}
            onBulkAssign={handleBulkAssign}
            onViewSiteGroups={(siteId, siteName) => setViewingGroups({ kind: 'site', id: siteId, name: siteName })}
          />
        </div>
      </main>

      {/* Dialogs */}
      <MobileConfigConflictDialog
        isOpen={!!(mobileConflict && conflictConfig && conflictNode)}
        configName={conflictConfig?.name ?? ''}
        fromNodeName={mobileConflict?.fromNodeName ?? ''}
        toNodeName={conflictNode?.name ?? ''}
        onConfirm={confirmMobileConflict}
        onCancel={() => setMobileConflict(null)}
      />

      <Notification type="success" isOpen={showSaveToast} content="Progress saved" />

      <AlertDialog
        isOpen={pendingNavTarget !== null}
        onClose={cancelNav}
        kind="warning"
        title="Unsaved changes"
        confirmProps={{
          label: 'Leave without saving',
          onClick: proceedNav,
        }}
        cancelProps={{
          label: 'Go back',
          onClick: cancelNav,
        }}
      >
        <p>You have unsaved changes on this page. Save before leaving or your progress won't be recorded.</p>
      </AlertDialog>
    </div>
  );
};

export default OrgAssignmentPage;
